/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, {
    useEffect, useCallback, useRef, useReducer,
} from 'react';
import {
    object, string, shape, arrayOf, number, func,
} from 'prop-types';
import { useDispatch } from 'react-redux';
import BYOProductItems from './BYOProductItems';
import { setSelectedBYOItems } from '../../../../../../../../state/ducks/App/App-Actions';
import { trackEvent } from '../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const BYOAddItems = ({
    cyoData,
    nonCountableKey,
    selectedItemId,
    brandId,
    product,
    showErrorMessage,
    setSelectedComponents,
    maxSelectableItem,
    selectedItems,
    compileSelectedItems,
    setNonCountableItems,
    maxSelectableForSku,
    setMaxSelectableForSku,
}) => {
    const errorTimeout = useRef();
    const selectedBYODispatch = useDispatch();
    const selectedAddItems = selectedItems?.addItems || {};
    const [error, setError] = useReducer((state, action) => {
        const { [action.itemID]: item, ...rest } = state;
        switch (action.type) {
            case 'add':
                return { ...state, [action.itemID]: action.message };
            case 'remove':
                return rest;
            default:
                return state;
        }
    }, {});
    useEffect(() => {
        setSelectedComponents(compileSelectedItems());
    }, [selectedItems]);

    // Don't allow add to cart if the product is CYO/PYO and doesn't have data in it.
    if (
        product?.productType === 'CUSTOM_ASSORTMENT'
        && (!cyoData?.cyoGroup || cyoData?.cyoGroup.length === 0)
    ) {
        return showErrorMessage('center');
    }

    const getGroupSelectionData = (groupName) => {
        let totalNumberSelected = 0;
        let groupNumberSelected = 0;
        let groupFlavorsSelected = 0;
        Object.entries(selectedAddItems).forEach(([name, items]) => {
            const addToGroupTotal = name === groupName;
            Object.values(items).forEach((item) => {
                if (item.quantity) {
                    totalNumberSelected += item.quantity;
                    if (addToGroupTotal) {
                        groupFlavorsSelected += 1;
                        groupNumberSelected += item.quantity;
                    }
                }
            });
        });
        return { totalNumberSelected, groupNumberSelected, groupFlavorsSelected };
    };

    const handleItemRemove = () => {
        const { addItems, ...updatedData } = selectedItems;
        selectedBYODispatch(setSelectedBYOItems(updatedData));
    };

    const handleRemoveError = (itemID) => {
        setError({ type: 'remove', itemID });
    };
    const checkItems = (items, data) => {
        const matchedDetails = [];
        const allMatch = items.every((item) => {
            const { name = '', minimumUnitsRequiredForComponent = '' } = item || {};
            const dataEntry = data?.addItems?.[name];
            if (!dataEntry && name) {
                matchedDetails.push({ name, unitRequired: minimumUnitsRequiredForComponent });
                return false;
            }
            const totalQuantity = Object.values(dataEntry)
                .filter((prop) => typeof prop === 'object' && 'quantity' in prop)
                .reduce((sum, prop) => sum + prop.quantity, 0);

            if (totalQuantity >= minimumUnitsRequiredForComponent) {
                return true;
            }
            matchedDetails.push({ name, unitRequired: minimumUnitsRequiredForComponent });
            return false;
        });
        return {
            allMatch,
            matchedDetails,
        };
    };

    // productCode is item.id, while itemID is item.legacyId
    const handleItemSelection = (cyoGroup, itemID, productname, productCode, incrementNumber, isInput = false) => {
        const groupName = cyoGroup?.name || '';
        let shouldAllowModification = true;
        let groupCompleted = false;
        const { totalNumberSelected, groupNumberSelected, groupFlavorsSelected } = getGroupSelectionData(groupName);
        // The number of allowable items per group
        const groupAllowableItems = cyoGroup?.numberofUnitsRequiredForComponent || 999;
        const filteredItems = cyoData?.cyoGroup?.filter((item) => item?.minimumUnitsRequiredForComponent > 0 && item?.type !== 'cyoBands');
        const selectedItem = filteredItems.find((item) => item.name === cyoGroup.name);
        const totalUnitRequired = filteredItems.reduce((total, item) => total + item.minimumUnitsRequiredForComponent, 0);

        // Only allow modification if not exceeding maximum limitations and not going negative
        if (incrementNumber > 0) {
            // The total number of allowable items regardless of group or flavor
            const totalAllowableItems = cyoData.maxQuantity || 999;

            // The number of allowable items per item
            const numFlavorsAllowed = cyoGroup?.maxFlavorsForComponent || 999;
            const unitRequiredData = filteredItems.length > 0 ? checkItems(filteredItems, selectedItems) : {};
            const allowAddedItem = totalAllowableItems - totalUnitRequired;

            if (totalUnitRequired > 0 && !selectedItem && !unitRequiredData?.allMatch && allowAddedItem === totalNumberSelected) {
                shouldAllowModification = false;
                return setError(
                    { type: 'add', itemID, message: `Please choose ${unitRequiredData.matchedDetails[0]?.name}` },
                );
            }
            if (totalNumberSelected + incrementNumber > totalAllowableItems) {
                shouldAllowModification = false;
                return setError({ type: 'add', itemID, message: `You can select up to ${totalAllowableItems} items` });
            }
            if (groupNumberSelected + incrementNumber > groupAllowableItems) {
                shouldAllowModification = false;
                return setError({ type: 'add', itemID, message: `You can select up to ${groupAllowableItems}` });
            }
            if (groupNumberSelected + incrementNumber === groupAllowableItems) {
                groupCompleted = true;
            } else {
                groupCompleted = false;
            }

            if (
                groupFlavorsSelected >= numFlavorsAllowed
                && (
                    !selectedAddItems[groupName]
                    || !selectedAddItems[groupName][itemID]
                    || selectedAddItems[groupName][itemID].quantity === 0
                )
            ) {
                shouldAllowModification = false;
                return setError({ type: 'add', itemID, message: `You can select up to ${numFlavorsAllowed} different flavors` });
            }
        } else if (
            !selectedAddItems[groupName]
            || !selectedAddItems[groupName][itemID]
            || selectedAddItems[groupName][itemID].quantity === 0
            || selectedAddItems[groupName][itemID].quantity - incrementNumber < 0
        ) {
            shouldAllowModification = false;
            return null;
        }

        if (shouldAllowModification) {
            const groupSelection = selectedAddItems[groupName] || {};
            const itemNumber = groupSelection[itemID]?.quantity || 0;
            const newTotal = itemNumber + incrementNumber;
            if (Number.isInteger(newTotal)) {
                const updatedSelection = {
                    ...selectedItems,
                    addItems: {
                        ...selectedItems?.['addItems'],
                        [groupName]: {
                            ...groupSelection,
                            completed: groupCompleted,
                            [itemID]: {
                                quantity: itemNumber + incrementNumber,
                                productCode,
                                productname,
                            },
                            groupProductSelected: groupNumberSelected + incrementNumber,
                        },
                        totalProductSelected: selectedAddItems?.totalProductSelected ? selectedAddItems?.totalProductSelected + incrementNumber : incrementNumber,
                    },
                };
                selectedBYODispatch(setSelectedBYOItems(updatedSelection));
                if (isInput) {
                    selectedBYODispatch(trackEvent({
                        eventCategory: 'BYO',
                        eventAction: 'Item Updated',
                        eventLabel: `${itemID} - ${newTotal}` || '',
                    }));
                }
            }
            if (groupNumberSelected + incrementNumber <= groupAllowableItems) {
                return setError({ type: 'remove', itemID });
            }
        }
        return null;
    };

    const resetState = useCallback(() => {
        setMaxSelectableForSku(maxSelectableItem());
    }, [cyoData]);

    // Reset state upon data change
    useEffect(() => {
        resetState();
    }, [selectedItemId]);

    // On unmount, clear timeouts to prevent memory leak
    useEffect(() => () => {
        if (typeof window !== 'undefined') {
            clearTimeout(errorTimeout);
        }
    });

    // Handles maintaining the nonCountable Items such as tongs and turkey
    useEffect(() => {
        const productArray = [];
        // Include any non-countable items such as turkey/tongs
        cyoData.cyoGroup.forEach((group) => {
            const item = group.components[0] || {};
            if (group.type === nonCountableKey) {
                productArray.push({
                    brandCode: brandId,
                    productCode: item.id,
                    productId: item.legacyId,
                    quantity: 1,
                    componentType: 'CYO',
                });
            }
        });
        setNonCountableItems(productArray);
    }, [cyoData]);
    return (
        <BYOProductItems
            totalProductSelected={selectedAddItems?.totalProductSelected}
            maxSelectableForSku={maxSelectableForSku}
            cyoData={cyoData}
            handleItemSelection={handleItemSelection}
            selectedItems={selectedItems}
            removeItem={handleItemRemove}
            error={error}
            handleRemoveError={handleRemoveError}
            maxSelectableItem={maxSelectableItem}
        />
    );
};

BYOAddItems.propTypes = {
    selectedItemId: string,
    brandId: string.isRequired,
    cyoData: shape({
        maxQuantity: number.isRequired,
        cyoGroup: arrayOf(
            shape({
                name: string.isRequired,
                components: arrayOf(
                    shape({
                        id: string.isRequired,
                        legacyId: string.isRequired,
                        name: string.isRequired,
                        image: shape({
                            name: string.isRequired,
                            path: string.isRequired,
                        }),
                    }),
                ).isRequired,
            }),
        ).isRequired,
    }).isRequired,
    product: object.isRequired,
    nonCountableKey: string.isRequired,
    showErrorMessage: func.isRequired,
    setSelectedComponents: func,
    maxSelectableItem: func,
    selectedItems: object,
    compileSelectedItems: func.isRequired,
    setNonCountableItems: func.isRequired,
    maxSelectableForSku: number,
    setMaxSelectableForSku: func,
};

BYOAddItems.defaultProps = {
    selectedItemId: null,
    setSelectedComponents: () => { },
    maxSelectableItem: () => { },
    selectedItems: {},
    maxSelectableForSku: 0,
    setMaxSelectableForSku: () => {},
};

export default BYOAddItems;
